import type { ReactNode } from "react";
import type { Components } from "react-markdown";
import { Children, isValidElement } from "react";
import { Link } from "@tanstack/react-router";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkGfm from "remark-gfm";

import { slugifyHeading } from "@/features/blog/blog-utils";
import { CodeFrame } from "@/landing/system/code";
import { Eyebrow } from "@/landing/system/layout";

const SVG_TAGS = [
  "svg",
  "g",
  "defs",
  "title",
  "desc",
  "path",
  "rect",
  "circle",
  "ellipse",
  "line",
  "polyline",
  "polygon",
  "text",
  "tspan",
  "use",
  "linearGradient",
  "radialGradient",
  "stop",
  "clipPath",
  "mask",
  "pattern",
  "marker",
  "symbol",
];

const blogSanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []),
    ...SVG_TAGS,
    "video",
    "source",
    "figure",
    "figcaption",
  ],
  attributes: {
    ...(defaultSchema.attributes ?? {}),
    "*": [
      ...((defaultSchema.attributes?.["*"] ?? []) as (
        | string
        | [string, ...unknown[]]
      )[]),
      "viewBox",
      "xmlns",
      "role",
      "aria-label",
      "fill",
      "stroke",
      "strokeWidth",
      "strokeOpacity",
      "strokeLinecap",
      "strokeLinejoin",
      "strokeDasharray",
      "opacity",
      "transform",
      "class",
      "className",
      "x",
      "y",
      "x1",
      "y1",
      "x2",
      "y2",
      "cx",
      "cy",
      "r",
      "rx",
      "ry",
      "d",
      "points",
      "width",
      "height",
      "textAnchor",
      "fontSize",
      "fontFamily",
      "fontWeight",
      "offset",
      "stopColor",
      "stopOpacity",
    ],
    video: [
      "src",
      "controls",
      "autoplay",
      "autoPlay",
      "muted",
      "loop",
      "playsinline",
      "playsInline",
      "poster",
      "preload",
      "width",
      "height",
    ],
    source: ["src", "type"],
  },
};

type CodeProps = { className?: string; children?: ReactNode };
type HastNode = { type: string; value?: string; children?: HastNode[] };

function extractLanguage(className: string | undefined): string | undefined {
  const match = className?.match(/language-(\S+)/);
  return match?.[1];
}

function extractCodeText(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return `${children}`;
  if (isValidElement<{ children?: ReactNode }>(children)) {
    return extractCodeText(children.props.children);
  }
  return Children.toArray(children).map(extractCodeText).join("");
}

function hastText(node: HastNode | undefined): string {
  if (!node) return "";
  if (node.type === "text") return node.value ?? "";
  return (node.children ?? []).map(hastText).join("");
}

function isBlank(node: HastNode) {
  return node.type === "text" && !node.value?.trim();
}

/**
 * Posts open callouts with a bold lead ("> **Key Takeaways**", "> **TL;DR:**").
 * Those render as glass callouts; a lead that is a whole paragraph becomes the
 * callout's mono label. Other blockquotes (quoted prompts) keep the hairline.
 */
function Blockquote({
  node,
  children,
}: {
  node?: HastNode;
  children?: ReactNode;
}) {
  const first = node?.children?.find((child) => !isBlank(child)) as
    | (HastNode & { tagName?: string })
    | undefined;
  const firstInline = first?.children?.filter((child) => !isBlank(child));
  const lead = firstInline?.[0] as
    | (HastNode & { tagName?: string })
    | undefined;
  if (first?.tagName !== "p" || lead?.tagName !== "strong") {
    return <blockquote>{children}</blockquote>;
  }

  if (firstInline?.length !== 1) {
    return <blockquote className="g-callout">{children}</blockquote>;
  }

  const items = Children.toArray(children);
  const labelIndex = items.findIndex((item) => isValidElement(item));
  return (
    <blockquote className="g-callout">
      <Eyebrow>{hastText(lead).replace(/:$/, "")}</Eyebrow>
      {items.filter((_, index) => index !== labelIndex)}
    </blockquote>
  );
}

const COMPONENTS: Components = {
  pre({ children }) {
    const items = Children.toArray(children);
    if (items.length === 1 && isValidElement<CodeProps>(items[0])) {
      const codeEl = items[0];
      const code = extractCodeText(codeEl.props.children).replace(/\n$/, "");
      return (
        <CodeFrame
          code={code}
          language={extractLanguage(codeEl.props.className)}
          className="my-[1.6em]"
        />
      );
    }
    return <pre>{children}</pre>;
  },
  h2({ node, children }) {
    return <h2 id={slugifyHeading(hastText(node))}>{children}</h2>;
  },
  h3({ node, children }) {
    return <h3 id={slugifyHeading(hastText(node))}>{children}</h3>;
  },
  blockquote({ node, children }) {
    return <Blockquote node={node}>{children}</Blockquote>;
  },
  table({ node: _node, ...props }) {
    return (
      <div className="g-table-scroll">
        <table {...props} />
      </div>
    );
  },
  img({ node: _node, src, alt, ...props }) {
    return <img {...props} src={src} alt={alt ?? ""} loading="lazy" />;
  },
  video({ node: _node, children, ...props }) {
    // Spread post attributes first so the controlled defaults below
    // (controls/playsInline/preload) always win and can't be disabled.
    return (
      <video
        {...props}
        style={{ maxHeight: "640px" }}
        controls
        playsInline
        preload="metadata"
      >
        {children}
      </video>
    );
  },
  a({ node: _node, href, children, ...props }) {
    if (!href) return <a {...props}>{children}</a>;
    if (href.startsWith("http://") || href.startsWith("https://")) {
      return (
        <a {...props} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }
    if (href.startsWith("#")) return <a href={href}>{children}</a>;
    return <Link to={href}>{children}</Link>;
  },
};

/** A post's markdown in Grid prose; the caller wraps it in `.g-prose`. */
export function BlogMarkdownRenderer({ content }: { content: string }) {
  return (
    <Markdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw, [rehypeSanitize, blogSanitizeSchema]]}
      components={COMPONENTS}
    >
      {content}
    </Markdown>
  );
}
